import {
  useQueries,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

export const APP_VERSION = process.env.REACT_APP_VERSION;
const BUILD_CHANNEL = process.env.REACT_APP_BUILD_CHANNEL;

export const isRunningLocally = () => {
  return process.env.NODE_ENV !== "production";
};

export const buildChannel = () => {
  if (BUILD_CHANNEL === "prod") {
    return "prod";
  }
  if (BUILD_CHANNEL === "dev-vm") {
    return "dev-vm";
  }
  return "dev";
};

export const sleepMs = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const isDefined = <X>(x: X | undefined): x is X => x !== undefined;

export const randomAlphaChar = () =>
  "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
export const randomAlphaString = (length: number) =>
  Array.from({ length }, randomAlphaChar).join("");

export const useRandomId = () => useMemo(() => randomAlphaString(8), []);
export const useRandomIds = (n: number) =>
  useMemo(() => Array.from({ length: n }, () => randomAlphaString(8)), [n]);

type UserNamePart = { full_name?: string | null; email: string };
export function displayNameForUser(user: UserNamePart): string;
export function displayNameForUser(
  user: UserNamePart | undefined
): string | undefined;
export function displayNameForUser(
  user: UserNamePart | undefined
): string | undefined {
  return user ? (user.full_name ?? user.email) : undefined;
}

// Automation accounts (service accounts, the clearance bot) that should be
// hidden from people pickers: assignee menus, user filters, and @-mentions.
// Long-term this belongs on the user record itself rather than an email
// convention.
export function isAutomationAccount(user: { email: string }): boolean {
  const email = user.email.toLowerCase();
  return ["service_account", "clearance"].some((pattern) =>
    email.includes(pattern)
  );
}

export const downloadBlob = (name: string, blob: Blob) => {
  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a link element and trigger a download
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useWindowSize = (): [number, number] => {
  const [windowSize, setWindowSize] = useState<[number, number]>([
    window.innerWidth,
    window.innerHeight,
  ]);
  useEffect(() => {
    const callback = () =>
      setWindowSize([window.innerWidth, window.innerHeight]);
    window.addEventListener("resize", callback);
    return () => window.removeEventListener("resize", callback);
  }, []);
  return windowSize;
};

export type KeysMatching<T, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

type IsPlainObject<T> = T extends object
  ? T extends unknown[]
    ? false
    : true
  : false;

export type LeafPaths<T, Prev extends string = ""> = {
  [K in keyof T]: IsPlainObject<T[K]> extends true
    ? LeafPaths<
        T[K],
        `${Prev}${Prev extends "" ? "" : "."}${Extract<K, string>}`
      >
    : `${Prev}${Prev extends "" ? "" : "."}${Extract<K, string>}`;
}[keyof T];

export type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExpandUnion<T> = T extends any ? Expand<T> : never;

export const useStoredValue = (
  key: string
): { value: string | null; set: (value: string) => void } => {
  const [count, setCount] = useState(0);

  const value = useMemo(() => localStorage.getItem(key), [key, count]);

  const set = (newValue: string) => {
    localStorage.setItem(key, newValue);
    setCount((c) => c + 1);
  };

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.key === key) {
        setCount((c) => c + 1);
      }
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }, [key]);

  return { value, set };
};

export const useDeepMemoValue = <T>(value: T): T => {
  const ref = useRef<T>(value);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
};

/**
 * `useQueries` with a sliding-window concurrency cap. Only `maxConcurrent`
 * queries are enabled at a time; as earlier ones settle (success or terminal
 * error), later queries flip from `enabled: false` to `enabled: true`.
 *
 * Pass `resetKey` that changes when the input set changes semantically (e.g.
 * different report or different chunk set).
 */
export const useBoundedQueries = <TData>(
  queryOptions: UseQueryOptions<TData>[],
  maxConcurrent: number,
  resetKey: unknown
): UseQueryResult<TData>[] => {
  const [doneCount, setDoneCount] = useState(0);
  useEffect(() => setDoneCount(0), [resetKey]);

  const queries = useQueries({
    queries: queryOptions.map((opt, i) => ({
      ...opt,
      enabled: i < doneCount + maxConcurrent,
    })),
  });

  const settledCount = queries.filter(
    (q) => q.data !== undefined || q.isError
  ).length;
  useEffect(() => {
    if (settledCount !== doneCount) setDoneCount(settledCount);
  }, [settledCount, doneCount]);

  return queries;
};
